import { Chip, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';

export const ResolutionChip = (props: {
  targetQueue: string;
  // A task is "unassigned" when it has neither an assigned resource nor a
  // required resource — regardless of queue (e.g. it can sit in auto_dispatch_q
  // yet still be unassigned). This is an additive overlay: the queue-based chip
  // below is unchanged; only what would otherwise render as the generic
  // "Processing" chip is relabeled "Unassigned" when no resource is present.
  assignedResourceId?: string | null;
  requiredResource?: unknown;
  kind?: ResolutionChipKind;
  tooltipString?: string;
}) => {
  const targetQueue = props.targetQueue;
  const kind = props.kind ?? 'default';

  const isUnassigned = !props.assignedResourceId && !props.requiredResource;
  const showUnassigned = isUnassigned && getLabel(targetQueue) === 'Processing';

  const label = showUnassigned ? 'Unassigned' : getLabel(targetQueue);
  const icon = showUnassigned ? (
    <PersonOffOutlinedIcon />
  ) : (
    getIcon(targetQueue)
  );

  if (kind === 'tooltip')
    return (
      <Chip
        label={label}
        sx={{ backgroundColor: 'background.paper' }}
        icon={icon}
      />
    );

  return (
    <Tooltip title={props.tooltipString ?? ''}>
      <Chip
        label={label}
        color={showUnassigned ? 'primary' : getColor(targetQueue)}
        size="small"
        icon={icon}
        variant="outlined"
      />
    </Tooltip>
  );
};

const getColor = (targetQueue: string) => {
  switch (targetQueue) {
    case 'egress':
      return 'success';
    case 'needs_attention_q':
    case 'manual_dispatch_q':
      return 'warning';
    case 'wip_q':
      return 'info';
    case 'cancelled_q':
      return 'error';
    default:
      return 'default';
  }
};

const getIcon = (targetQueue: string) => {
  switch (targetQueue) {
    case 'egress':
      return <CheckIcon />;
    case 'manual_dispatch_q':
      return <PersonSearchOutlinedIcon />;
    default:
      return <ErrorOutlineIcon />;
  }
};

export const getLabel = (targetQueue: string) => {
  switch (targetQueue) {
    case 'dispatched_q':
      return 'Dispatched';
    case 'egress':
      return 'Completed';
    case 'needs_attention_q':
      return 'Attention Needed';
    case 'wip_q':
      return 'Started';
    case 'cancelled_q':
      return 'Cancelled';
    case 'manual_dispatch_q':
      return 'Needs Dispatching';
    case 'input_q':
    case 'auto_dispatch_q':
    default:
      return 'Processing';
  }
};

type ResolutionChipKind = 'default' | 'tooltip';
